// LIBS
import { logger } from "../../../../libs/logger";
import { adform_campaign_metadata } from "../../../../libs/node";
// HELPERS
import { getReportingDateRestrictions } from "./getReportingDateRestrictions";
// TYPES
import { GetCampaignDataProps } from "../types";

/**
 * Grabs campaign data specific to the creatives listed in the specific advertiser for Adform report.
 * Per product requirement, if a user requests a campaign report in the specific advertiser page, all creatives which have tags within that advertiser should be included in the report.
 */
export async function getCampaignData({ getAllCreativesData, user, advertiserId, advertiserName, setAdformReportingModalPresetData, setAdformReportingModalOpen, notificationDispatch }: GetCampaignDataProps) {
  let campaignId;
  /* Grab creative object data for every creative saved in specific advertiser */
  let allCreativesData = await getAllCreativesData({ variables: { teamId: user.teamId, advertiserId: advertiserId } });
  /* Grab all campaign ids found in each creative object fetched in previous step */
  const allCampaignIdsWithinAdvertiser = allCreativesData.data.readAllCreativesAdvertiser.map((creative: any, i: number) => {
    return creative.adserver.campaignId;
  });
  /* If no campaign IDs were found in previous step then exit process */
  if (!allCampaignIdsWithinAdvertiser.length) throw new Error("No campaigns found. Please ensure you have requested tags for creatives within this campaign.");
  /* Filters out duplicate campaign IDs within an advertiser's list of campaign IDs leaving us with an array of zero duplicates */
  const uniqueCampaignIdsWithinAdvertiser = allCampaignIdsWithinAdvertiser.filter((id: string, i: number) => {
    return allCampaignIdsWithinAdvertiser.indexOf(id) === i;
  });
  /* If there is only one campaignID found then assign campaignId to that value */
  if (uniqueCampaignIdsWithinAdvertiser.length === 1) {
    if (uniqueCampaignIdsWithinAdvertiser.includes("")) {
      return notificationDispatch({
        type: "ADD",
        payload: {
          header: "Error",
          message: "No campaigns found. Please ensure you have requested tags for creatives within this campaign.",
          type: "error",
          autoDismiss: false,
        },
      });
    } else {
      campaignId = uniqueCampaignIdsWithinAdvertiser[0];
    }
  }
  /**
   *  If multiple campaign IDs are found, get the campaign which matches the advertiser name.
   *  This may change in future (detail in bug card 245923). - KT 30.12.22
   *  https://www.notion.so/picnicmedia/Studio-Adform-Reporting-multiple-Campaign-ids-within-one-Advertiser-75d7fd2afa93426f87fd467cf7619e49 - JA 16.06.23
   * */
  if (uniqueCampaignIdsWithinAdvertiser.length > 1) {
    const campaignsData = await Promise.all(
      /**
       * First remove all empty values that may possibly be included in array
       * Then for each campaign ID, make a post request to our campaign metadata API to collect the metadata for the campaign
       * */
      uniqueCampaignIdsWithinAdvertiser
        .filter((v: string) => v)
        .map(async (campaign: string) => {
          try {
            /* Only run campaign metadata API if campaign ID exists for advertiser */
            if (!campaign) {
              return notificationDispatch({
                type: "ADD",
                payload: {
                  header: "Error",
                  message: "No campaigns found. Please ensure you have requested tags for creatives within this campaign.",
                  type: "error",
                  autoDismiss: false,
                },
              });
            } else {
              return await adform_campaign_metadata("campaign", campaign);
            }
          } catch (error) {
            logger({ error, user, context: { origin: "getCampaignData_getCampaignData" } });
            return notificationDispatch({
              type: "ADD",
              payload: {
                header: "Error",
                message: error.message,
                type: "error",
                autoDismiss: false,
              },
            });
          }
        }),
    );
    /* Find the first campaign object in campaignsData that matches the advertiser name after performing some regex */
    const campaign = campaignsData.find((campaign) => {
      /* If the campaign name, with all lowercase letters and non-alphanumeric characters removed, matches the advertiser name (parsed under the same regex), return the campaign object */
      if (campaign.name.toLowerCase().replace(/[^A-Z0-9]/gi, "") === advertiserName.toLowerCase().replace(/[^A-Z0-9]/gi, "")) return campaign;
    });
    /* If no campaign name matches the advertiser name, then throw error */
    if (!campaign) throw new Error("No campaign name exists which matches this advertiser name.");
    /* Assign campaignId the value of the chosen campaign's id property */
    campaignId = campaign.id;
  }
  /* Make a POST request to our campaign metadata API to collect the metadata for this campaign */
  const campaignData = await adform_campaign_metadata("campaign", campaignId);
  /* This allows us to set the correct start and current date for our calendar inside the modal */
  const { minDate, maxDate } = getReportingDateRestrictions("campaign", campaignData);
  setAdformReportingModalPresetData({ scope: "campaign", name: advertiserName, id: campaignId, dateRangeRestriction: { start: minDate, end: maxDate } });
  /* Our modal can open up now that we have all the initial information required set */
  setAdformReportingModalOpen(true);
}
